import { FC, useCallback, useEffect } from 'react'
import { cn } from '@bem-react/classname'
import { parse, stringify } from 'query-string'
import { yupResolver } from '@hookform/resolvers/yup'
import { Helmet } from 'react-helmet'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { Memo } from '@/hoc/Memo'
import yup from '@/utils/yup-extended'
import useBackendError from '@/hooks/useBackendError'
import {
  licenseVersionsPriceWithOptionsThunk,
  setLicenseVersion,
} from '@/core/services/LicenseVersions/store/license-versions.slice'
import { useAppDispatch } from '@/core/store/hooks'
import { getItem, setItem } from '@/utils/persistanceStorage'
import { ByDiscountCodeRequestModel } from './models/LicensesModel'

import { Back } from '@/components/Back'
import { Button } from '@/components/Button'
import { Field } from '@/components/form/Field'
import { FormGroup } from '@/components/form/FormGroup'
import { Title } from '@/components/Title'

import './styles/RenewPage.scss'

const cnRenewPage = cn('RenewPage')

export const RenewPage: FC = Memo(() => {
  const { t } = useTranslation('translation')
  const { search } = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [setBackendError, fieldsError, resetErrors] = useBackendError()

  const RenewSchema = yup.object().shape({
    discountCode: yup.string().trim().nullable(true),
  })

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<Pick<ByDiscountCodeRequestModel, 'discountCode'>>({
    mode: 'onChange',
    resolver: yupResolver(RenewSchema),
  })

  useEffect(() => {
    dispatch(setLicenseVersion(null))
    const form = getItem('form')
    if (form && form.discountCode) {
      reset({ discountCode: form.discountCode })
    }
  }, [])

  const renew = useCallback(
    async (data: Pick<ByDiscountCodeRequestModel, 'discountCode'>) => {
      const s = parse(search)
      const licenseId = s.licenseId ? (s.licenseId as string) : ''
      const discountCode = data.discountCode
      try {
        await dispatch(
          licenseVersionsPriceWithOptionsThunk({
            discountCode,
            licenseId,
          }),
        ).unwrap()

        const query = stringify({ ...s, discountCode })
        navigate(`/cabinet/licenses/subscription?${query}`)
        setItem('form', { ...s, discountCode })
      } catch (errors) {
        setBackendError(errors)
      }
    },
    [],
  )

  useEffect(() => {
    const subscription = watch((form, { type }) => {
      if (
        type === 'change' &&
        form &&
        form.discountCode &&
        form.discountCode.length === 0
      ) {
        resetErrors && resetErrors()
      }
    })
    return () => subscription.unsubscribe()
  }, [watch])

  return (
    <>
      <Helmet>
        <title>{t('Renew')} | Registration</title>
      </Helmet>
      <div className={cnRenewPage()}>
        <Back />
        <form className={cnRenewPage('form')} onSubmit={handleSubmit(renew)}>
          <Title className={cnRenewPage('title')}>
            {t(
              'Please enter the Discount Code if you have or continue without it:',
            )}
          </Title>
          <FormGroup>
            <Field
              {...register('discountCode')}
              placeholder={t('Please enter Discount code')}
              label={t('Please enter Discount code')}
              errors={
                (errors.discountCode && errors.discountCode.message) ||
                (fieldsError &&
                  fieldsError.discountCode &&
                  fieldsError.discountCode.message)
              }
            />
          </FormGroup>
          <div className={cnRenewPage('text')}>
            {t('Сlick on the button to proceed to the payment process')}
          </div>
          <Button large type='submit' isLoading={isSubmitting}>
            {t('Next')}
          </Button>
        </form>
      </div>
    </>
  )
})
